import { KlydoTimes } from "../Types";
import { Author } from "./author";
import { baseClient } from "./baseRequest";
import { CalendarEvent } from "./calendarEvent";

export enum SoundMode {
  OFF = "OFF",
  ON = "ON",
  FIRST_LOOP = "FIRST_LOOP",
}

export enum AlarmType {
  ALARM = "ALARM",
  REPEAT = "REPEAT",
  REMINDER = "REMINDER",
}

// Helper function to check if sound is enabled (ON or FIRST_LOOP)
export const hasSound = (soundMode?: SoundMode): boolean => {
  return soundMode === SoundMode.ON || soundMode === SoundMode.FIRST_LOOP;
};

/**
 * The klydo discriminator. `SealedKlydo` is a one-way terminal lock meaning
 * "finalized and servable" — reached once via either route (the public review
 * was approved, or the klydo belongs to an approved collection) and never left
 * again. It is deliberately NOT a review verdict: what gets served publicly is
 * decided by `public.status`, not by this.
 */
export enum KlydoType {
  Draft = "DraftKlydo",
  /**
   * The real stored value since the TK-506 migration renamed every document.
   * Ships in the same coordinated release as the api's flip — against a
   * pre-migration api this value matches nothing.
   */
  Sealed = "SealedKlydo",
}

/**
 * States of a klydo's PUBLICATION request. There is deliberately no draft
 * member: "never asked to publish" is the ABSENCE of `public.reviewStatus.state`,
 * not a state inside it. Withdrawing keeps the record — `message`/`comment` stay
 * so a rejection can still be read and answered — and clears only the state, so
 * a klydo can carry a reviewStatus whose state is undefined.
 */
export enum PublicationReviewStates {
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected",
}

/**
 * Who picked the go-live date in `requestPayload`.
 *
 * ⚠️ ABSENT MEANS `Artist` — every request written before this field existed came
 * from the studio, and the artist path still omits it. Read it as
 * `source ?? Artist`.
 *
 * `Admin` is a date an admin set from the backoffice on a klydo that never made a
 * publication request (the normal case for a klydo sealed by its collection). The
 * verdict on such a record is a real admin approval; what is synthetic is the
 * REQUEST it approves, and this field is the only thing that says so.
 */
export enum PublicationRequestSource {
  Artist = "artist",
  Admin = "admin",
}

/**
 * Klydo publication-review status, nested under `public.reviewStatus`. Workflow
 * and audit only — it does NOT gate public serving (see `KlydoPublic.status`)
 * and is absent on klydos whose publication was never requested.
 * `message`/`updatedAt` are set server-side, so they are optional on write
 * payloads.
 */
export type KlydoReviewStatus = {
  state: PublicationReviewStates;
  message?: string;
  /** The artist's reply to `message`, stored when they resubmit. */
  comment?: string;
  /** Epoch ms of the last review-status change; also the "approved on" time. */
  updatedAt?: number;
  /**
   * Frozen legacy field, no longer written on approval. Read `updatedAt` for the
   * approval time.
   */
  approvedAt?: number;
  /**
   * What was ASKED for: the go-live date the artist (or an admin) requested. This
   * is the only place a publication date lives now — it is the answer to "on what
   * date?", never to "is it public?" (that is `KlydoPublic.status`).
   *
   * `null` means no standing request: never picked, cancelled, or an admin took
   * the klydo down.
   */
  requestPayload?: {
    timestamp: number | null;
    /** See `PublicationRequestSource`. Absent means the artist asked. */
    source?: PublicationRequestSource;
  };
};

/**
 * The klydo's public facet. `status` is THE public serving gate — a stored
 * boolean meaning "served publicly right now", replacing the old
 * `public.timestamp <= Date.now()` comparison every read site used to do for
 * itself. The requested publication DATE now lives in
 * `reviewStatus.requestPayload.timestamp`.
 */
export type KlydoPublic = {
  status: boolean;
  reviewStatus?: KlydoReviewStatus;
};

/**
 * A klydo update body for `PUT /admin/klydos/:id`. The `public` facet is partial
 * all the way down: the api flattens the payload into Mongo dot paths, so
 * sending only `public.status` or only `public.reviewStatus.requestPayload`
 * leaves the siblings alone. A `null` leaf `$unset`s that path.
 *
 * Spelled out instead of `Partial<Klydo>`, which only makes the top level
 * optional and would demand a full `public` object on every write.
 */
export type KlydoUpdatePayload = Omit<Partial<Klydo>, "public"> & {
  public?: {
    status?: boolean;
    reviewStatus?: Partial<KlydoReviewStatus> | null;
  };
};

export type Theme = {
  backgroundColor: string;
  handsColor: string;
  pendulumColor: string;
  pendulumRodColor: string;
  dialsColor?: string;
};

export type FeartureTimes = {
  start: string;
  end: string;
};

interface KlydoAnimationSizes {
  xs: string;
  s: string;
  m: string;
  l: string;
}
// Same values as the API's PendulumLogoType: "sized" (default) or "full".
export type PendulumLogoType = "sized" | "full";

export type Klydo = {
  name: string;
  subtitle?: string;
  klydoAnimation?: KlydoAnimationSizes;
  tags: string[];
  loopUrl: string;
  chimesUrls?: string[];
  strikesUrls?: string[];
  alarmUrls?: string[];
  theme: Theme;
  times: KlydoTimes[]; // Not used currently. was in firestore
  id: string;
  creator: string;
  dials: boolean;
  klydoSecureUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  unlisted: boolean;
  inTasks: number;
  type: KlydoType;
  public: KlydoPublic;
  pendulumLogo?: string | null;
  // "sized" (default) or "full": the logo fills the whole bob, untinted.
  pendulumLogoType?: PendulumLogoType;
  holdPendulumLogo?: boolean;
  author: Author;
  metadata: {
    fileName: string;
    klydoDurationInSeconds: number;
    chimesDurationInSeconds?: number;
    generatedByAI?: boolean;
    chimesFileName?: string;
    alarmFileName?: string;
    alarmDurationInSeconds?: number;
    loopUrlChecksum?: string;
  };
  stats: {
    favorites: number;
    viewed: number;
    time: number;
  };
  greet?: boolean;
  capabilitiesV2: {
    animationSound: {
      mode: SoundMode;
      playOnceConfig?: {
        frequency?: number | null;
        random?: boolean | null;
      };
    };
    alarm?: {
      type: AlarmType;
    };
  };
  collectionId?: string;
  licensing?: Array<{ expirationDate: string; description: string }>;
  artistsRating?: {
    active: boolean;
    startedAt?: string;
    endDate?: string;
  };
};

export type KlydoRatingStats = {
  klydoId: string;
  average: number;
  count: number;
};

import { Label } from "./label";
export type KlydoLabel = Label;

export type AnalyticsRow = {
  clock: string;
  favorite: boolean;
  clock_time: number;
  display_time: number;
  auto_switch: boolean;
  collection_type: number;
};

export type KlydoSearchFilter = {
  unlisted?: boolean;
  excludeIds?: string[];
  scheduled?: boolean;
  inReview?: boolean;
  /**
   * Publicly served right now, or not — a plain equality on `public.status`.
   * Replaces the old `publicTimestamp: { timestamp, operator }`, which the
   * backoffice only ever called with a sentinel 1/0 to ask this same yes/no
   * question.
   */
  isPublic?: boolean;
  greet?: boolean;
  inCollection?: boolean;
  hasCustomChime?: boolean;
  generatedByAI?: boolean;
  tags?: string[];
  tagsMatchMode?: "all" | "any";
  ratingStage?: boolean;
};

export type GetCalendarEventKlydosInput = {
  country?: string;
  year: number;
  month: number;
  day?: number;
  state?: string;
};

export type GetCalendarEventKlydosResponse = {
  calendarEvent: CalendarEvent;
  klydos: Klydo[];
};

export type KlydoApiClient = {
  updateKlydo: (id: string, klydo: KlydoUpdatePayload) => Promise<void>;
  publicationReviewTextSearch: (
    query: string,
    limit: number,
    skip: number,
    parseData?: boolean,
    filters?: KlydoSearchFilter,
    sort?: { [key: string]: number },
  ) => Promise<Klydo[]>;

  similaritySearch: (
    query: string,
    filters: KlydoSearchFilter,
    pageSize?: number,
    useGeminiEmbedding?: boolean,
  ) => Promise<Klydo[]>;

  similaritySearchByImage: (
    image: string,
    filters: KlydoSearchFilter,
    pageSize?: number,
    useGeminiEmbedding?: boolean,
  ) => Promise<Klydo[]>;

  sealedTextSearch: (
    query: string,
    limit: number,
    skip: number,
    parseData?: boolean,
    filters?: KlydoSearchFilter,
    sort?: { [key: string]: number },
  ) => Promise<{ data: Klydo[]; total: number }>;

  textSearch: (
    query: string,
    limit: number,
    skip: number,
    parseData?: boolean,
    filters?: KlydoSearchFilter,
    sort?: { [key: string]: number },
    type?: KlydoType,
  ) => Promise<{ data: Klydo[]; total: number }>;

  getKlydosAnalytics: (
    klydoId: string[],
    deviceId: string,
  ) => Promise<AnalyticsRow[]>;
  getKlydoById: (klydoId: string) => Promise<Klydo>;
  getKlydosByIds: (ids: string[]) => Promise<Klydo[]>;
  getKlydoRatingStats: (klydoId: string) => Promise<KlydoRatingStats>;
  getKlydoRatingStatsBatch: (klydoIds: string[]) => Promise<KlydoRatingStats[]>;
  approvePublication: (
    klydoId: string,
    message: string,
    timestamp?: number,
  ) => Promise<void>;
  rejectPublication: (klydoId: string, message: string) => Promise<void>;
  sealKlydo: (klydoId: string) => Promise<void>;
  updateDraftKlydo: (klydoId: string, klydo: Partial<Klydo>) => Promise<void>;

  getCalendarEventKlydos: ({
    country,
    year,
    month,
    day,
    state,
  }: GetCalendarEventKlydosInput) => Promise<GetCalendarEventKlydosResponse[]>;

  getKlydoLabels: ({
    query,
    skip,
    limit,
    sort,
  }: {
    query: string;
    skip?: number;
    limit?: number;
    sort?: Record<string, 1 | -1>;
  }) => Promise<KlydoLabel[]>;
  createKlydoLabel: (data: { text: string }) => Promise<KlydoLabel>;
  createKlydoLabelsBulk: (data: { texts: string[] }) => Promise<KlydoLabel[]>;
  updateKlydoLabel: (labelId: string, text: string) => Promise<KlydoLabel>;
  deleteKlydoLabel: (labelId: string) => Promise<void>;
  getAllKlydoLabels: () => Promise<KlydoLabel[]>;
};

const klydoApiClient: KlydoApiClient = {
  updateKlydo: async (id, klydo) => {
    return baseClient.put({
      url: `/admin/klydos/${id}`,
      body: klydo,
    });
  },
  similaritySearch: async (query, filters, pageSize, useGeminiEmbedding) => {
    return baseClient.post<Klydo[]>({
      url: `/admin/klydos/.search/similarity/sealed`,
      body: {
        query: query,
        filters: filters,
        pageSize: pageSize,
        useGeminiEmbedding: useGeminiEmbedding,
      },
    });
  },
  similaritySearchByImage: async (
    image,
    filters,
    pageSize,
    useGeminiEmbedding,
  ) => {
    return baseClient.post<Klydo[]>({
      url: `/admin/klydos/.search/similarity/sealed`,
      body: {
        image,
        filters,
        pageSize,
        useGeminiEmbedding,
      },
    });
  },
  sealedTextSearch: async (
    query,
    limit,
    skip,
    includeMeteData,
    filters,
    sort,
  ) => {
    return baseClient.post<{ data: Klydo[]; total: number }>({
      url: `/admin/klydos/.search/text/sealed?query=${encodeURIComponent(query)}`,
      body: {
        limit: limit,
        skip: skip,
        includeMeteData: includeMeteData,
        filters: filters,
        sort: sort,
      },
    });
  },
  textSearch: async (
    query,
    limit,
    skip,
    includeMeteData,
    filters,
    sort,
    type,
  ) => {
    return baseClient.post<{ data: Klydo[]; total: number }>({
      url: `/admin/klydos/.search/text?query=${encodeURIComponent(query)}`,
      body: {
        limit: limit,
        skip: skip,
        includeMeteData: includeMeteData,
        filters: filters,
        sort: sort,
        type: type,
      },
    });
  },
  publicationReviewTextSearch: async (
    query,
    limit,
    skip,
    includeMeteData,
    filters,
    sort,
  ) => {
    return baseClient.post<Klydo[]>({
      url: `/admin/klydos/.search/publication-review?query=${encodeURIComponent(query)}`,
      body: {
        limit: limit,
        skip: skip,
        filters: filters,
        sort: sort,
      },
    });
  },

  getKlydosAnalytics: async (klydoId: string[], deviceId: string) => {
    return baseClient.post<AnalyticsRow[]>({
      url: `/klydos/get-klydos-analytics`,
      body: {
        klydoId,
        deviceId,
      },
    });
  },

  getKlydoById: async (klydoId: string) => {
    return baseClient.get<Klydo>({
      url: `/admin/klydos/${klydoId}`,
    });
  },

  getKlydoRatingStats: async (klydoId: string) => {
    return baseClient.get<KlydoRatingStats>({
      url: `/admin/rating-stats/${klydoId}`,
    });
  },

  getKlydoRatingStatsBatch: async (klydoIds: string[]) => {
    return baseClient.post<KlydoRatingStats[]>({
      url: `/admin/rating-stats`,
      body: { klydoIds },
    });
  },

  getKlydosByIds: async (ids: string[]) => {
    // The ids ride in the GET query string — chunk so a large selection
    // (validate-before-send, pasted id lists) stays under common URL/header
    // caps (a few hundred ~20-char ids pass 8KB and draw a 414/431).
    const CHUNK_SIZE = 100;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      chunks.push(ids.slice(i, i + CHUNK_SIZE));
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        baseClient.get<Klydo[]>({
          url: `/admin/klydos/klydos-by-ids?ids=${chunk.join(",")}`,
        }),
      ),
    );
    return results.flat();
  },

  /**
   * `timestamp` (epoch ms) is the ADMIN-SCHEDULING path: it sets the go-live date
   * as part of the approval, for a klydo with no approved request of its own. A
   * date written on its own would never fire — the api's hourly publish cron only
   * looks at records whose `reviewStatus.state` is `approved`. The stored request
   * is marked `source: "admin"` so it does not read as an artist request nobody
   * made. Omit it to approve a standing request as-is.
   */
  approvePublication: async (
    klydoId: string,
    message: string,
    timestamp?: number,
  ) => {
    return baseClient.put({
      url: `/admin/klydos/publication/approve/${klydoId}`,
      body: { message, timestamp },
    });
  },
  rejectPublication: async (klydoId: string, message: string) => {
    // The dedicated endpoint, not update-draft. It stamps
    // public.reviewStatus.updatedAt server-side, which is what the studio's
    // "status changed" dot reads — routing a reject through the generic draft
    // update left that stale, so artists were never notified. It also 404s on a
    // missing klydo instead of silently succeeding.
    return baseClient.put({
      url: `/admin/klydos/publication/reject/${klydoId}`,
      body: { message },
    });
  },
  sealKlydo: async (klydoId: string) => {
    // Seal only — flips the klydo to SealedKlydo and promotes its assets to
    // the permanent folders. No review verdict, no public serving, no creator
    // promotion. One-way: nothing un-seals.
    return baseClient.put({
      url: `/admin/klydos/seal/${klydoId}`,
    });
  },
  updateDraftKlydo: async (klydoId: string, klydo: Partial<Klydo>) => {
    return baseClient.put({
      url: `/admin/klydos/update-draft/${klydoId}`,
      body: { klydo },
    });
  },

  getCalendarEventKlydos: async ({
    country,
    year,
    month,
    day,
    state,
  }: GetCalendarEventKlydosInput) => {
    let url = `/admin/klydos/calendar-events-klydos?year=${year}&month=${month}`;

    if (day) url += `&day=${day}`;
    if (country) url += `&country=${country}`;
    if (state) url += `&state=${state}`;

    return baseClient.get<GetCalendarEventKlydosResponse[]>({ url });
  },

  getKlydoLabels: async ({ query, skip, limit, sort }) => {
    return baseClient.post<KlydoLabel[]>({
      url: `/admin/klydos/labels/.search`,
      body: {
        query,
        skip,
        limit,
        sort,
      },
    });
  },
  createKlydoLabel: async (data) => {
    return baseClient.post<KlydoLabel>({
      url: `/admin/klydos/labels`,
      body: data,
    });
  },
  createKlydoLabelsBulk: async (data) => {
    return baseClient.post<KlydoLabel[]>({
      url: `/admin/klydos/labels/bulk`,
      body: data,
    });
  },
  updateKlydoLabel: async (labelId, text) => {
    return baseClient.put<KlydoLabel>({
      url: `/admin/klydos/labels/${labelId}`,
      body: { text },
    });
  },
  deleteKlydoLabel: async (labelId) => {
    return baseClient.delete<void>({
      url: `/admin/klydos/labels/${labelId}`,
    });
  },
  getAllKlydoLabels: async () => {
    return baseClient.get<KlydoLabel[]>({
      url: `/admin/klydos/labels`,
    });
  },
};
export default klydoApiClient;
